// registration.component.ts
import { Component, NgZone, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../guards/service/auth.service';
import { FreelancerDataService } from '../../../services/freelancer-data.service';
import { ToastrService } from 'ngx-toastr';
import { CommonModule, formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { DomainService } from '../../domain/domain.service';
import { allCountries } from 'country-telephone-data';
import { MdbModalService, MdbModalRef } from 'mdb-angular-ui-kit/modal';

@Component({
  selector: 'app-registration',
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './registration.component.html',
  styleUrl: './registration.component.css',
  providers: [MdbModalService]
})
export class RegistrationComponent implements OnInit {
  MobileCountries = allCountries.map(c => ({
    name: c.name,
    dialCode: c.dialCode,
    iso2: c.iso2
  }));

  signUpForm!: UntypedFormGroup;
  countries: any;
  consultantRegistration: any;
  workExperience: any;

  email: any;
  password: any;
  hidePasswordField = false;

  private readonly JWT_TOKEN = 'token';
  communityId: any;
  slug: string | null = null;

  aboutMeLength = 0;

  // Resume
  private readonly RESUME_MAX_BYTES = 5 * 1024 * 1024;
  resumeFile: File | null = null;
  resumeName: string | null = null;

  // Availability
  availability: any = null;
  @ViewChild('availabilityModal') availabilityModal!: TemplateRef<any>;
  modalRef: MdbModalRef<any> | null = null;
  availabilityForm!: UntypedFormGroup;
  availabiltyDurations: any;

  constructor(
    private fb: UntypedFormBuilder,
    private authGuardService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private dataService: FreelancerDataService,
    private toastrService: ToastrService,
    private ngZone: NgZone,
    public domainService: DomainService,
    private modalService: MdbModalService,
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.slug = params['slug'];
      const rawCommunityId = params['communityId'];
      this.email = params['email'] || '';
      const storedPassword = sessionStorage.getItem('tempPassword') || '';
      this.password = storedPassword;
      this.hidePasswordField = !!storedPassword;

      if (this.slug) localStorage.setItem('slug', this.slug);
      if (rawCommunityId) {
        this.communityId = +rawCommunityId;
        localStorage.setItem('communityId', rawCommunityId);
      }
    });

    this.initializeSignInForm();
    this.getConsultantRegistrationDataList();
    this.initializeAvailabilityAddForm();

    this.signUpForm.get('is_recruiter')!.valueChanges.subscribe(on => {
      this.toggleSeekerValidators(!!on);
    });
    this.toggleSeekerValidators(this.isRecruiter);
  }

  get isRecruiter(): boolean {
    return this.signUpForm?.get('is_recruiter')?.value === true;
  }

  // ---------- Form init / validators ----------
  initializeSignInForm(): void {
    const passwordControl = new UntypedFormControl(
      this.password,
      this.hidePasswordField ? [] : Validators.required
    );
    const providerIdFromStorage = Number.isInteger(Number(sessionStorage.getItem('providerId')))
      ? Number(sessionStorage.getItem('providerId'))
      : 5;

    this.signUpForm = this.fb.group({
      is_recruiter: new UntypedFormControl(false),

      name: new UntypedFormControl('', Validators.required),
      designations: new UntypedFormControl('', Validators.required),
      experience_years_id: new UntypedFormControl(null, Validators.required),
      email: new UntypedFormControl(this.email, [Validators.required, Validators.email]),
      password: passwordControl,
      country_id: new UntypedFormControl(null, Validators.required),
      provider_id: new UntypedFormControl(providerIdFromStorage, Validators.required),
      industry_id: new UntypedFormControl(99, Validators.required),
      country_code: new UntypedFormControl(null),
      phone: new UntypedFormControl(null),
      about_me: new UntypedFormControl(null, Validators.maxLength(2500))
    });
  }

  // make public
  toggleSeekerValidators(isRecruiter: boolean) {
    const seekerRequired = ['experience_years_id', 'country_id'];
    const maybeRequired = ['designations'];

    seekerRequired.forEach(key => {
      const c = this.signUpForm.get(key)!;
      if (isRecruiter) c.clearValidators(); else c.setValidators([Validators.required]);
      c.updateValueAndValidity({ emitEvent: false });
    });

    maybeRequired.forEach(key => {
      const c = this.signUpForm.get(key)!;
      if (isRecruiter) c.clearValidators(); else c.setValidators([Validators.required]);
      c.updateValueAndValidity({ emitEvent: false });
    });
  }

  // ---------- Helpers ----------
  private onlyDigits(v: any): string {
    return String(v ?? '').replace(/\D+/g, '');
  }
  private normalizeDialCode(code: any): string | null {
    const digits = this.onlyDigits(code);
    if (!digits) return null;
    return '+' + digits.replace(/^0+/, '');
  }
  private buildPhoneForBackend(raw: { country_code?: any; phone?: any }): {
    country_code: string | null; phone: string | null;
  } {
    const cc = this.normalizeDialCode(raw.country_code);
    const phoneDigits = this.onlyDigits(raw.phone);
    const phone = phoneDigits || null;
    return { country_code: cc, phone };
  }
  private arrayify(v: any): any[] {
    if (Array.isArray(v)) return v;
    const s = (v ?? '').toString().trim();
    return s ? [s] : [];
  }

  // ---------- Data fetch ----------
  getConsultantRegistrationDataList(): any {
    this.dataService.getconsultantRegistration().subscribe({
      next: (res) => {
        this.consultantRegistration = res?.data;
        this.workExperience = this.consultantRegistration?.experienceYears;
        this.countries = this.consultantRegistration?.countries;

        const raw = this.consultantRegistration?.durations
          ?? this.consultantRegistration?.availabilityDurations
          ?? [];

        this.availabiltyDurations = raw.map((d: any) => ({
          duration_id: Number(d.duration_id),
          value: d.value ?? d.label ?? d.name ?? String(d.duration_id)
        }));
      },
      error: () => { }
    });
  }

  // ---------- Submit ----------
  registrationSubmit(): void {
    const rawValues = this.signUpForm.value;

    const phonePack = this.buildPhoneForBackend({
      country_code: rawValues.country_code,
      phone: rawValues.phone
    });

    const normalizedDesignations = this.arrayify(rawValues.designations)
      .map((d: any) => String(d).trim())
      .filter((d: string) => d.length > 0);

    const baseClean: any = {
      ...rawValues,
      is_recruiter: !!rawValues.is_recruiter,
      experience_years_id: rawValues.experience_years_id ? +rawValues.experience_years_id : null,
      country_id: rawValues.country_id ? +rawValues.country_id : null,
      industry_id: +rawValues.industry_id,
      designations: normalizedDesignations,          // KEEP for recruiter
      country_code: phonePack.country_code,
      phone: phonePack.phone                         // no phone_e164
    };

    if (baseClean.is_recruiter) {
      baseClean.experience_years_id = null;
      baseClean.country_id = null;
      delete baseClean.availability;
      baseClean.workAuthorizations = [];
      baseClean.skills = [];
      baseClean.certifications = [];
      baseClean.medicals = [];
      // DO NOT blank designations
    }

    const availabilityPayload = this.buildAvailabilityPayloadForBackend(this.availability);
    if (!this.isRecruiter && availabilityPayload) {
      baseClean.availability = availabilityPayload;
    }

    const formData = Object.entries(baseClean).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && !(typeof value === 'string' && value.trim() === '')) {
        (acc as any)[key] = value;
      }
      return acc;
    }, {} as any);

    if (this.domainService.isCommunityDomain() && this.slug && this.communityId) {
      formData.community_id = +this.communityId;

      this.dataService.registerCommunityUser(formData).subscribe({
        next: (res: any) => {
          const token = res?.data?.token;
          if (token) {
            localStorage.setItem(this.JWT_TOKEN, token);
            const sidFromJwt = this.authGuardService.getSpaceIdFromToken();
            if (sidFromJwt) localStorage.setItem('spaceID', String(sidFromJwt));
            sessionStorage.removeItem('tempPassword');
            sessionStorage.removeItem('providerId');
            this.afterTokenAdopted();
          }
          this.handleRegistrationSuccess(rawValues);
        },
        error: (error: { error: { error: { description: string } } }) => {
          this.toastrService.error(error?.error?.error?.description || 'Registration failed.');
        }
      });
    } else {
      this.dataService.registerUser(formData).subscribe({
        next: (res: any) => {
          const token = res?.data?.token;
          if (token) {
            localStorage.setItem(this.JWT_TOKEN, token);
            sessionStorage.removeItem('tempPassword');
            sessionStorage.removeItem('providerId');
            this.afterTokenAdopted();
          }
          this.handleRegistrationSuccess(rawValues);
        },
        error: (error: { error: { error: { description: string } } }) => {
          this.toastrService.error(error?.error?.error?.description || 'Registration failed.');
        }
      });
    }
  }

  // ---------- Post-registration ----------
  private afterTokenAdopted(): void {
    try {
      (this.authGuardService as any)?.emitAuthChange?.();
      (this.dataService as any)?.resetCache?.();
      (this.dataService as any)?.clear?.();
      window.dispatchEvent(new StorageEvent('storage', { key: this.JWT_TOKEN }));
    } catch { }
  }

  private getSpaceHeader(): string {
    let sid = localStorage.getItem('spaceID') || '';
    if (!sid) {
      const fromJwt = this.authGuardService.getSpaceIdFromToken();
      if (fromJwt) {
        sid = String(fromJwt);
        localStorage.setItem('spaceID', sid);
      }
    }
    return sid || '';
  }

  private doUploadThenRedirect(): void {
    if (!this.resumeFile) {
      this.redirectAfterLogin();
      return;
    }
    const fd = new FormData();
    fd.append('resume', this.resumeFile, this.resumeName || 'resume.pdf');
    const headerValue = this.getSpaceHeader();

    this.dataService.uploadResume(fd, headerValue).subscribe({
      next: () => {
        this.toastrService.success('Resume uploaded.');
        this.redirectAfterLogin();
      },
      error: () => {
        this.toastrService.error('Resume upload failed. You can upload it later from your profile.');
        this.redirectAfterLogin();
      }
    });
  }

  private handleRegistrationSuccess(rawValues: any): void {
    const email = rawValues.email;
    const password = rawValues.password;
    const providerId = rawValues.provider_id;

    const proceed = () => this.doUploadThenRedirect();

    if (password) {
      const loginData = { email, password, provider_id: providerId };
      this.authGuardService.login(loginData).subscribe({
        next: () => { this.afterTokenAdopted(); proceed(); },
        error: (error: HttpErrorResponse) => {
          let errorMessage = 'An unexpected error occurred during login.';
          if (error.error?.description) errorMessage = error.error.description;
          if (error.error?.error?.description) errorMessage = error.error.error.description;
          this.toastrService.error(errorMessage, 'Login Failed');
          proceed();
        }
      });
    } else {
      proceed();
    }
  }

  // ---------- About me / phone / resume ----------
  onAboutMeChange(): void {
    this.aboutMeLength = this.signUpForm?.get('about_me')?.value?.length || 0;
  }
  triggerResumeBrowse(): void {
    const el = document.getElementById('resume_input') as HTMLInputElement | null;
    el?.click();
  }
  async onResumeFileChange(evt: Event): Promise<void> {
    const input = evt.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file) { this.resumeFile = null; this.resumeName = null; return; }

    const extOk = /\.pdf$/i.test(file.name);
    const mimeOk = file.type === 'application/pdf' || file.type === '';
    const headerOk = await this.isTrulyPdf(file);

    if (!(extOk && mimeOk && headerOk)) {
      this.toastrService.error('Only genuine PDF files are accepted.');
      input.value = '';
      return;
    }
    if (file.size > this.RESUME_MAX_BYTES) {
      this.toastrService.error('PDF must be 5 MB or smaller.');
      input.value = '';
      return;
    }

    this.resumeFile = file;
    this.resumeName = file.name;
  }
  clearResume(): void {
    this.resumeFile = null;
    this.resumeName = null;
    const el = document.getElementById('resume_input') as HTMLInputElement | null;
    if (el) el.value = '';
  }
  private isTrulyPdf(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const bytes = new Uint8Array(reader.result as ArrayBuffer);
        const sig = String.fromCharCode(...bytes.slice(0, 5));
        resolve(sig === '%PDF-');
      };
      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(file.slice(0, 5));
    });
  }

  // ---------- Availability ----------
  openAvailabilityPopup(): void {
    if (this.availability) {
      this.availabilityForm.patchValue({
        mode: this.availability.mode || 'now',
        start_at: this.availability.start_at || null,
        duration_id: this.availability.duration_id ?? null
      }, { emitEvent: false });
    } else {
      this.availabilityForm.reset({ mode: 'now', start_at: null, duration_id: null }, { emitEvent: false });
    }

    this.modalRef = this.modalService.open(this.availabilityModal, {
      modalClass: 'modal-lg custom-modal',
      backdrop: true,
      ignoreBackdropClick: true,
      animation: true
    });
  }
  initializeAvailabilityAddForm() {
    this.availabilityForm = this.fb.group({
      mode: ['now'],
      start_at: [null],
      duration_id: [null as number | null]
    });
  }
  closeModal(): void { this.modalRef?.close(); }
  onModeChange(): void {
    const mode = this.availabilityForm.get('mode')?.value;
    if (mode === 'now') this.availabilityForm.patchValue({ start_at: null });
    else if (mode === 'date') this.availabilityForm.patchValue({ duration_id: null });
  }
  saveAvailability(): void {
    const val = this.availabilityForm.value;

    if (val.mode === 'now') {
      const durationId = val.duration_id === null || val.duration_id === undefined ? null : Number(val.duration_id);
      this.availability = {
        mode: 'now',
        duration_id: durationId,
        duration_label: this.getDurationLabel(durationId),
        start_at: null
      };
    } else {
      this.availability = {
        mode: 'date',
        start_at: val.start_at ? new Date(val.start_at).toISOString() : null,
        duration_id: null,
        duration_label: ''
      };
    }
    this.closeModal();
  }
  onAvailabilitySave(data: any): void { this.availability = data || null; }
  getDurationLabel(id: number | string | null | undefined): string {
    const num = id === '' || id === null || id === undefined ? null : Number(id);
    if (num === null || Number.isNaN(num) || num === 8) return '';
    const found = this.availabiltyDurations?.find((d: any) => Number(d.duration_id) === num);
    return found?.value ?? '';
  }
  private buildAvailabilityPayloadForBackend(av: any): { start_at: string | null; duration_id: number | null } | null {
    if (!av) return null;
    const normId =
      av.duration_id === null || av.duration_id === undefined || av.duration_id === ''
        ? null
        : Number(av.duration_id);
    const toYmd = (val: any): string | null => {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      return formatDate(d, 'yyyy-MM-dd', 'en-MY');
    };
    if (av.mode === 'now') {
      const today = formatDate(new Date(), 'yyyy-MM-dd', 'en-MY');
      return { start_at: `${today} 00:00:00.000`, duration_id: normId };
    } else {
      const ymd = toYmd(av.start_at);
      return { start_at: ymd ? `${ymd} 00:00:00.000` : null, duration_id: null };
    }
  }

  // ---------- Redirect ----------
  redirectAfterLogin() {
    const slug = localStorage.getItem('slug');
    const communityId = localStorage.getItem('communityId');
    this.ngZone.run(() => {
      if (this.domainService.isCommunityDomain() && slug && communityId) {
        this.router.navigate([`/${slug}/${communityId}/home`]);
      } else {
        this.router.navigate(['/profile']);
      }
    });
  }
}
